import {useCallback, useEffect, useRef, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {createCheckoutSession, validateStripeSession} from '../../api/stripeApi';
import {ExercisePreview} from '../../components/app/ExercisePreview';
import {TutorialOverlay} from '../../components/tutorial/TutorialOverlay';
import {VRScene} from '../../scenes/VRScene';
import {useAuthStore} from '../../store/authStore';
import {useSavedSequencesStore} from '../../store/savedSequencesStore';
import {ExerciseSettings, RangeSetting, TrainingMode} from '../../types/exercises';
import {getUserSessionMetrics, SessionMetrics} from '../../utils/firebase';

const LandingPage = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #121212;
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Courier New', monospace;
`;

const Content = styled.main`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
    overflow: auto;
  }
`;

const WebXRBanner = styled.div`
  position: fixed;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 600px;
  width: calc(100% - 3rem);
  background: linear-gradient(135deg, rgba(18, 18, 18, 0.95) 0%, rgba(30, 30, 30, 0.95) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0.875rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    top: 1rem;
    width: calc(100% - 2rem);
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;

  @media (max-width: 768px) {
    gap: 0.625rem;
  }
`;

const BannerDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4444;
  box-shadow: 0 0 8px rgba(255, 68, 68, 0.6);
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.4;
      transform: scale(0.8);
    }
  }
`;

const BannerText = styled.div`
  flex: 1;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  line-height: 1.5;
  font-weight: 400;
  letter-spacing: 0.01em;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }

  strong {
    display: block;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.1rem;
    letter-spacing: -0.01em;

    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }
`;

const BannerCloseButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.8);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SideNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 45px;
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const NavItem = styled.button<{$active?: boolean}>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${props => (props.$active ? 'rgba(0, 0, 0, 0.3)' : 'transparent')};
  color: ${props => (props.$active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.4)')};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: rgba(255, 255, 255, 0.8);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Container = styled.div`
  background: #1e1e1e;
  border-radius: 24px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4), 0 8px 16px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 1200px;
  height: 90vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 100%;
  }
`;

const TitleBar = styled.div`
  height: 32px;
  background: #2a2a2a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-radius: 24px 24px 0 0;
  position: relative;
`;

const AppTitle = styled.div`
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 0.75rem;
  font-weight: 600;
  color: #00ff88;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const PlayButton = styled.button<{$isDisabled?: boolean}>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.05);
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 24px solid rgba(255, 255, 255, 0.4);
    border-top: 16px solid transparent;
    border-bottom: 16px solid transparent;
    margin-left: 4px;
    transition: all 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    border-color: #00ff88;
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 0 30px rgba(0, 255, 136, 0.4), 0 0 60px rgba(0, 255, 136, 0.2);

    &::after {
      border-left-color: #00ff88;
    }
  }

  &:active {
    transform: translate(-50%, -50%) scale(1.05);
  }

  ${props =>
    props.$isDisabled &&
    `
    opacity: 0.5;
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.1);

    &::after {
      border-left-color: rgba(255, 255, 255, 0.2);
    }

    &:hover {
      background: rgba(255, 255, 255, 0.02);
      border-color: rgba(255, 255, 255, 0.1);
      transform: translate(-50%, -50%) scale(1);
      box-shadow: none;

      &::after {
        border-left-color: rgba(255, 255, 255, 0.2);
      }
    }

    &:active {
      transform: translate(-50%, -50%) scale(1);
    }
  `}
`;

const PreviewSection = styled.div`
  flex: 1;
  width: 100%;
  min-height: 320px;
  overflow: hidden;
  background: #000000;
  position: relative;
`;

const SplitViewContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  gap: 1px;
  background: rgba(255, 255, 255, 0.1);
`;

const SplitViewPanel = styled.div`
  flex: 1;
  position: relative;
  background: #000000;
`;

const EyeLabel = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const InfoButton = styled.button`
  height: 28px;
  padding: 0 0.75rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.05);
  }

  &:active {
    color: rgba(255, 255, 255, 0.6);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Toolbar = styled.div`
  padding: 1.5rem 2.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  background: #1e1e1e;
  flex-shrink: 0;
  align-items: start;

  @media (max-width: 768px) {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    max-height: 50vh;
  }
`;

const BackButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 2px;
  border-radius: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.15s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
    transform: translateX(-2px);
  }

  &:active {
    transform: translateX(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EyePreviewButton = styled.button<{$state: string}>`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  border: none;
  background: ${props => {
    if (props.$state.includes('red')) return 'rgba(255, 0, 0, 0.15)';
    if (props.$state.includes('green')) return 'rgba(0, 255, 0, 0.15)';
    return 'rgba(255, 255, 255, 0.03)';
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => {
      if (props.$state.includes('red')) return 'rgba(255, 0, 0, 0.2)';
      if (props.$state.includes('green')) return 'rgba(0, 255, 0, 0.2)';
      return 'rgba(255, 255, 255, 0.05)';
    }};
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: ${props => (props.$state.includes('-x') ? '"✕"' : '""')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffff00;
    font-size: 1.5rem;
    font-weight: bold;
  }

  &::after {
    content: '';
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => {
      if (props.$state.includes('red')) return '#ff0000';
      if (props.$state.includes('green')) return '#00ff00';
      return '#ffffff';
    }};
  }
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  width: 100%;
`;

const EyeControlsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column: 1 / -1;
  gap: 1rem;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }

  & > * {
    @media (max-width: 768px) {
      width: 100%;
    }
  }
`;

const ControlLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.01em;
  text-align: left;
  margin-bottom: 0.25rem;
  display: block;
`;

const SettingButtonGroup = styled.div`
  position: relative;
  width: 100%;
  height: 48px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const SettingButton = styled.button<{$active?: boolean}>`
  flex: 1;
  height: 100%;
  border-radius: 6px;
  border: none;
  background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.15)' : 'transparent')};
  color: ${props => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)')};
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: capitalize;

  &:hover {
    background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.08)')};
    color: ${props => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.7)')};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DebugSection = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.3);
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  border-radius: 0 0 24px 24px;
`;

const DebugHeader = styled.div`
  padding: 0.5rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const DebugTitle = styled.span`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
`;

const DebugToggle = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: ${props => (props.className?.includes('expanded') ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

const GuideContainer = styled.div`
  padding: 3rem 4rem;
  height: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const GuideSection = styled.section`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GuideHeading = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #00ff88;
  margin: 0 0 1rem 0;
  letter-spacing: -0.01em;
`;

const GuideText = styled.p`
  font-size: 0.85rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 1rem 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepsGrid = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.5rem;
`;

const StepCard = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: 0.5rem;

    .step-number {
      background: rgba(0, 255, 136, 0.2);
      border-color: rgba(0, 255, 136, 0.4);
    }
  }
`;

const StepNumber = styled.div`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.2);
  color: #00ff88;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.2s ease;
`;

const StepTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
`;

const StepDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const ExercisesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SettingItem = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const SettingName = styled.span`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  min-width: 100px;
  font-size: 0.8rem;
`;

const SettingDescription = styled.span`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 0.8rem;
`;

const ComparisonTable = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1rem;
  max-width: 800px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ComparisonCard = styled.div<{$highlight?: boolean; $clickable?: boolean}>`
  background: ${props => (props.$highlight ? 'rgba(0, 255, 136, 0.05)' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${props => (props.$highlight ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease;

  ${props =>
    props.$clickable &&
    `
    &:hover {
      background: rgba(0, 255, 136, 0.08);
      border-color: rgba(0, 255, 136, 0.3);
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(0, 255, 136, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  `}
`;

const ComparisonTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const ComparisonPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #00ff88;
  margin: 0;
`;

const ComparisonPriceNote = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: -0.5rem;
`;

const ComparisonFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ComparisonFeature = styled.li`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  padding-left: 1.25rem;
  position: relative;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #00ff88;
    font-weight: bold;
  }
`;

const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AuthInput = styled.input`
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: #00ff88;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const AuthButton = styled.button<{$isSignup?: boolean}>`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${props =>
    props.$isSignup
      ? 'linear-gradient(135deg, #ffaa44 0%, #ff8822 100%)'
      : 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)'};
  border: ${props => (props.$isSignup ? '2px solid rgba(255, 170, 68, 0.5)' : 'none')};
  border-radius: 8px;
  color: #000000;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  box-shadow: ${props =>
    props.$isSignup
      ? '0 2px 10px rgba(255, 170, 68, 0.2), 0 0 20px rgba(255, 170, 68, 0.3), inset 0 0 10px rgba(255, 170, 68, 0.1)'
      : '0 2px 10px rgba(0, 255, 136, 0.2)'};

  &:hover {
    background: ${props =>
      props.$isSignup
        ? 'linear-gradient(135deg, #ffbb66 0%, #ff9933 100%)'
        : 'linear-gradient(135deg, #00ffaa 0%, #00dd7a 100%)'};
    transform: translateY(-1px);
    box-shadow: ${props =>
      props.$isSignup
        ? '0 4px 15px rgba(255, 170, 68, 0.3), 0 0 30px rgba(255, 170, 68, 0.4), inset 0 0 15px rgba(255, 170, 68, 0.15)'
        : '0 4px 15px rgba(0, 255, 136, 0.3)'};
    border-color: ${props => (props.$isSignup ? 'rgba(255, 170, 68, 0.8)' : 'transparent')};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const AuthToggle = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 1.5rem;
  padding: 0.5rem;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    color: #00ff88;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const SavedMotionsContainer = styled.div`
  padding: 3rem 4rem;
  height: 100%;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const AccountSection = styled.section`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AccountHeading = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #00ff88;
  margin: 0 0 1rem 0;
`;

const AccountInfo = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.5rem;
`;

const AccountLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const AccountValue = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 0.625rem 1rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 3rem;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.6);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1rem;
  }
`;

const SavedMotionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const SavedMotionCard = styled.button`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SavedMotionTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
`;

const SavedMotionMeta = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.75rem;
`;

const SavedMotionSettings = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SavedMotionTag = styled.span`
  padding: 0.25rem 0.625rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  opacity: 0.3;
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const CompactEmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
`;

const CompactEmptyStateIcon = styled.div`
  font-size: 1rem;
  opacity: 0.4;
`;

const CompactEmptyStateText = styled.p`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const SavedMotionsScroll = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 0.75rem 2.5rem 0.75rem 2.5rem;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const SavedMotionCompactCard = styled.button<{$isActive?: boolean}>`
  flex-shrink: 0;
  width: 180px;
  background: ${props => (props.$isActive ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${props => (props.$isActive ? 'rgba(0, 255, 136, 0.4)' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;

  &:hover {
    background: ${props => (props.$isActive ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 255, 255, 0.05)')};
    border-color: ${props => (props.$isActive ? 'rgba(0, 255, 136, 0.5)' : 'rgba(255, 255, 255, 0.15)')};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 16px;
  height: 16px;
  background: rgba(255, 50, 50, 0.8);
  border: 1px solid rgba(255, 50, 50, 1);
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;
  color: white;
  font-size: 0.7rem;
  line-height: 1;

  &:hover {
    background: rgba(255, 50, 50, 1);
    transform: scale(1.1);
  }
`;

const CompactMotionTitle = styled.h4`
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactMotionMeta = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 0.35rem;
`;

const CompactMotionTags = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const CompactMotionTag = styled.span`
  padding: 0.2rem 0.45rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.7);
`;

const PositionControlsOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PositionIndicator = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  backdrop-filter: blur(8px);
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
`;

const VerticalPositionBar = styled.div`
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  height: 60%;
  width: 4px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  border-radius: 2px;
  pointer-events: auto;
  cursor: grab;
  transition: width 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);

  &:active {
    cursor: grabbing;
  }
`;

const HorizontalPositionBar = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  border-radius: 2px;
  pointer-events: auto;
  cursor: grab;
  transition: height 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);

  &:active {
    cursor: grabbing;
  }
`;

const PositionHandle = styled.div<{$isDragging?: boolean}>`
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: radial-gradient(circle, #ffffff 0%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 0.4) 100%);
  border: 2px solid #ffffff;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.6), 0 0 40px rgba(255, 255, 255, 0.3),
    inset 0 0 10px rgba(255, 255, 255, 0.4);
  cursor: grab;
  transition: all 0.2s ease;
  pointer-events: auto;
`;

const SaveButton = styled.button`
  height: 28px;
  padding: 0 0.75rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 1;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.05);
  }

  &:active {
    color: rgba(255, 255, 255, 0.6);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const ModalCard = styled.div`
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.6);
`;

const ModalHeading = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
`;

const ModalText = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: #00ff88;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ModalButton = styled.button<{$variant?: 'primary' | 'secondary'}>`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: ${props => (props.$variant === 'primary' ? '#00ff88' : 'rgba(255, 255, 255, 0.05)')};
  color: ${props => (props.$variant === 'primary' ? '#000000' : 'rgba(255, 255, 255, 0.7)')};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.$variant === 'primary' ? '#00ffaa' : 'rgba(255, 255, 255, 0.08)')};
    color: ${props => (props.$variant === 'primary' ? '#000000' : '#ffffff')};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const UpgradeCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 170, 68, 0.15) 0%, rgba(255, 136, 34, 0.15) 100%);
  border: 2px solid rgba(255, 170, 68, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0 20px rgba(255, 170, 68, 0.2), inset 0 0 10px rgba(255, 170, 68, 0.1);
`;

const UpgradeCardHeader = styled.h3`
  color: #ffaa44;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-align: left;
`;

const UpgradeCardText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  text-align: left;
`;

const UpgradeButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #ffaa44 0%, #ff8822 100%);
  color: #000000;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 170, 68, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 170, 68, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MotionCard = styled.button`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ExerciseCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
`;

const MotionIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 2rem;
`;

const MotionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;
`;

const MotionDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.5;
`;

const MotionSelectorWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const MotionSelectorCard = styled.button`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-align: left;
  width: 100%;
  height: 48px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MotionDropdown = styled.div`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  overflow: hidden;
`;

const MotionDropdownItem = styled.button<{$active?: boolean}>`
  width: 100%;
  padding: 0.75rem;
  background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.08)' : 'transparent')};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${props => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.8)')};
  font-size: 0.8rem;
  font-weight: 500;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MotionDropdownIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const MotionArrow = styled.div<{$expanded?: boolean}>`
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 0.5rem;
  transition: transform 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    transform: ${props => (props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.2s ease;
  }
`;

const MotionSelectorContent = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
`;

const MotionSelectorTitle = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
`;

const ProBadge = styled.span`
  font-size: 0.55rem;
  font-weight: 700;
  color: #ffaa44;
  background: rgba(255, 170, 68, 0.2);
  border: 1px solid rgba(255, 170, 68, 0.5);
  border-radius: 4px;
  padding: 0.2rem 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  box-shadow: 0 0 8px rgba(255, 170, 68, 0.4), inset 0 0 4px rgba(255, 170, 68, 0.2);
  pointer-events: none;
`;

const LockedSettingButton = styled(SettingButton)<{$locked?: boolean}>`
  position: relative;
  opacity: ${props => (props.$locked ? 0.6 : 1)};
  cursor: ${props => (props.$locked ? 'pointer' : 'pointer')};

  &:hover {
    opacity: ${props => (props.$locked ? 0.75 : 1)};
  }
`;

const ProOverlayWrapper = styled.div`
  position: absolute;
  right: 0.25rem;
  top: 0;
  bottom: 0;
  width: calc(50% - 0.125rem);
  background: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  backdrop-filter: blur(2px);
`;

const MotionLibraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 1.5rem;
  padding: 2rem;
  height: 100%;
  overflow: hidden;
`;

const motionData: Record<TrainingMode, {title: string; description: string; icon: string}> = {
  convergence: {
    title: 'Convergence',
    description: 'Train your eyes to converge on close targets',
    icon: '⚬',
  },
  pursuit: {
    title: 'Pursuit',
    description: 'Smooth tracking of continuously moving targets',
    icon: '↻',
  },
  saccades: {
    title: 'Saccades',
    description: 'Quick eye movements between stationary points',
    icon: '↔',
  },
  accommodation: {
    title: 'Accommodation',
    description: 'Focus adjustment and depth perception training',
    icon: '◎',
  },
  figure8: {
    title: 'Figure 8',
    description: 'Follow smooth figure-8 pattern movements',
    icon: '∞',
  },
};

export default function Home() {
  const siteUrl = import.meta.env.VITE_APP_DOMAIN || 'https://simplevision.app';
  const location = useLocation();
  const navigate = useNavigate();

  // Auth stores
  const {
    user,
    login,
    register,
    logout,
    isLoading: authLoading,
    error: authError,
    profile,
    updateSubscription,
  } = useAuthStore();
  const {
    savedSequences,
    loadSavedSequences,
    saveSequence,
    deleteSequence,
    isLoading: sequencesLoading,
  } = useSavedSequencesStore();

  // State for exercise settings
  const [speed, setSpeed] = useState(7.5); // 0 to 15 scale (7.5 is middle)
  const [range, setRange] = useState<RangeSetting>('medium');
  // Duration in minutes (1-15) or -1 for unlimited. Default is 1 minute.
  const [durationMinutes, setDurationMinutes] = useState(1);
  const [showVRScene, setShowVRScene] = useState(false);
  const [isSplitView] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [selectedMotionId, setSelectedMotionId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [motionToDelete, setMotionToDelete] = useState<{id: string; name: string} | null>(null);
  const [showProModal, setShowProModal] = useState(false);
  // Check if returning from Stripe checkout - initialize to account view if so
  const params = new URLSearchParams(location.search);
  const hasStripeSession = params.get('session_id');

  const [currentView, setCurrentView] = useState<'active' | 'guide' | 'motions' | 'saved'>(
    hasStripeSession ? 'saved' : 'active'
  );
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [selectedMotion, setSelectedMotion] = useState<TrainingMode>('convergence');
  const [showMotionDropdown, setShowMotionDropdown] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // Session metrics state
  const [sessionMetrics, setSessionMetrics] = useState<{
    currentStreak: number;
    timeInSession: {
      today: number;
      thisWeek: number;
      thisMonth: number;
      thisYear: number;
    };
    totalSessions: number;
    lastSessionDate: Date | null;
    sessions: SessionMetrics[];
  } | null>(null);

  // WebXR support detection
  const [isWebXRSupported, setIsWebXRSupported] = useState<boolean | null>(null);
  const [showWebXRBanner, setShowWebXRBanner] = useState(true);

  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Save exercise state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showPostExerciseSaveModal, setShowPostExerciseSaveModal] = useState(false);
  const [sequenceName, setSequenceName] = useState('');
  const [ballPosition, setBallPosition] = useState<{x: number; y: number; z: number}>({x: 0, y: 0, z: 0});
  const [isDraggingVertical, setIsDraggingVertical] = useState(false);
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);
  const verticalBarRef = useRef<HTMLDivElement>(null);
  const horizontalBarRef = useRef<HTMLDivElement>(null);
  const processingStripeSession = useRef(false);
  const processedSessionId = useRef<string | null>(null);

  // Individual eye settings - each cycles through: red, green, red+x, green+x
  type EyeState = 'red' | 'green' | 'red-x' | 'green-x';
  const [leftEyeState, setLeftEyeState] = useState<EyeState>('red');
  const [rightEyeState, setRightEyeState] = useState<EyeState>('green');

  // Speed settings: slow = 1, normal = 7.5, fast = 15
  type SpeedSetting = 'slow' | 'normal' | 'fast';
  const [speedSetting, setSpeedSetting] = useState<SpeedSetting>('normal');

  const handleSpeedChange = (setting: SpeedSetting) => {
    setSpeedSetting(setting);
    const speedValues = {slow: 1, normal: 7.5, fast: 15};
    setSpeed(speedValues[setting]);
  };

  const handleRangeChange = (newRange: RangeSetting) => {
    setRange(newRange);
  };

  // Duration handlers with pro checks
  const isPro = profile?.subscription?.plan === 'pro' && profile?.subscription?.status === 'active';

  const handleDecrementDuration = () => {
    setDurationMinutes(prev => Math.max(prev > 0 ? prev - 1 : -1, 1));
  };

  const handleIncrementDuration = () => {
    if (!isPro) {
      setShowProModal(true);
      return;
    }
    setDurationMinutes(prev => (prev === -1 ? 1 : Math.min(prev + 1, 15)));
  };

  const handleUnlimitedDuration = () => {
    if (!isPro) {
      setShowProModal(true);
      return;
    }
    setDurationMinutes(-1);
  };

  // Cycle through eye states: red -> green -> red-x -> green-x -> red
  const cycleLeftEyeState = () => {
    const states: EyeState[] = ['red', 'green', 'red-x', 'green-x'];
    const currentIndex = states.indexOf(leftEyeState);
    const nextIndex = (currentIndex + 1) % states.length;
    setLeftEyeState(states[nextIndex]);
  };

  const cycleRightEyeState = () => {
    const states: EyeState[] = ['red', 'green', 'red-x', 'green-x'];
    const currentIndex = states.indexOf(rightEyeState);
    const nextIndex = (currentIndex + 1) % states.length;
    setRightEyeState(states[nextIndex]);
  };

  // Convert eye states to color and overlay settings
  const getColorFromState = (state: EyeState): string => {
    return state.includes('red') ? '#ff0000' : '#00ff00';
  };

  const getOverlayFromState = (state: EyeState): boolean => {
    return state.includes('-x');
  };

  // Convert saved color and overlay settings back to EyeState
  const getStateFromSettings = (color: string, overlay: boolean): EyeState => {
    const isRed = color === '#ff0000' || color === 'red';
    if (isRed && overlay) return 'red-x';
    if (isRed && !overlay) return 'red';
    if (!isRed && overlay) return 'green-x';
    return 'green';
  };

  // Handle vertical drag with optimized calculation
  const handleVerticalDrag = useCallback((clientY: number) => {
    if (!verticalBarRef.current) return;
    const rect = verticalBarRef.current.getBoundingClientRect();
    const relativeY = (clientY - rect.top) / rect.height;
    const maxRange = 1.5;
    const newY = maxRange - relativeY * (maxRange * 2);
    const clampedY = Math.max(-maxRange, Math.min(maxRange, newY));
    setBallPosition(prev => ({...prev, y: clampedY}));
  }, []);

  // Handle horizontal drag with optimized calculation
  const handleHorizontalDrag = useCallback((clientX: number) => {
    if (!horizontalBarRef.current) return;
    const rect = horizontalBarRef.current.getBoundingClientRect();
    const relativeX = (clientX - rect.left) / rect.width;
    const maxRange = 1.5;
    const newX = (relativeX - 0.5) * (maxRange * 2);
    const clampedX = Math.max(-maxRange, Math.min(maxRange, newX));
    setBallPosition(prev => ({...prev, x: clampedX}));
  }, []);

  // Get settings for both eyes - used in split view
  const getBothEyeSettings = () => {
    return {
      isRedGreen: true,
      leftEyeColor: getColorFromState(leftEyeState),
      rightEyeColor: getColorFromState(rightEyeState),
      leftEyeOverlay: getOverlayFromState(leftEyeState),
      rightEyeOverlay: getOverlayFromState(rightEyeState),
    };
  };

  const handleStartVR = () => {
    if (isWebXRSupported === false) {
      // Show the WebXR banner if WebXR is not supported
      setShowWebXRBanner(true);
      return;
    }
    // Show VR scene - it will auto-enter VR when Canvas is ready
    setShowVRScene(true);
  };

  const openTutorial = () => {
    setCurrentView('active');
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
  };

  // Auth handlers
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password) {
      setFormError('Please enter both email and password');
      return;
    }

    try {
      await login(email, password);
      // Clear form on success
      setEmail('');
      setPassword('');
      // Stay on saved view to show saved motions
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password || !passwordConfirm) {
      setFormError('Please complete all fields');
      return;
    }

    if (password !== passwordConfirm) {
      setFormError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    try {
      // Store credentials for auto-login after Stripe redirect
      localStorage.setItem('pending_stripe_signup', JSON.stringify({email, password, timestamp: Date.now()}));

      // First, register the user with free plan
      await register(email, password, undefined, {
        plan: 'free',
        status: 'active',
      });

      // After successful registration, redirect to Stripe checkout
      // The success_url will bring them back here with session_id
      // The cancel_url will bring them back without completing payment
      await createCheckoutSession('monthly', undefined, user?.uid, email);

      // Clear form - user will be redirected to Stripe
      setEmail('');
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      console.error('Signup error:', err);
      // Clear pending signup on error
      localStorage.removeItem('pending_stripe_signup');
      setFormError('Signup failed. Please try again.');
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      await logout();
      setCurrentView('active');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Handle premium checkout
  const handlePremiumCheckout = async () => {
    try {
      await createCheckoutSession('monthly', undefined, user?.uid, user?.email || undefined);
    } catch (err) {
      console.error('Checkout error:', err);
    }
  };

  // Save exercise handler
  const handleSaveExercise = async () => {
    if (!user) {
      // If not logged in, redirect to saved view to show login
      setCurrentView('saved');
      setShowSaveModal(false);
      setShowPostExerciseSaveModal(false);
      return;
    }

    if (!sequenceName.trim()) {
      return;
    }

    try {
      await saveSequence(sequenceName, [
        {
          type: selectedMotion,
          duration: durationInSeconds,
          settings: {
            speed,
            range,
            ballPosition,
            ...getBothEyeSettings(),
          },
        },
      ]);

      // Clear name and close modal
      setSequenceName('');
      setShowSaveModal(false);
      setShowPostExerciseSaveModal(false);

      // Reload sequences to show the new one
      await loadSavedSequences();
    } catch (err) {
      console.error('Save exercise error:', err);
    }
  };

  const handleDeleteExercise = async () => {
    if (!motionToDelete) return;

    try {
      await deleteSequence(motionToDelete.id);
      setShowDeleteModal(false);
      setMotionToDelete(null);
      setSelectedMotionId(null);
      await loadSavedSequences();
    } catch (err) {
      console.error('Delete exercise error:', err);
    }
  };

  // Handler when VR scene closes
  const handleCloseVR = (finalBallPosition?: {x: number; y: number; z: number}) => {
    setShowVRScene(false);
    // Update ball position if provided from VR session
    if (finalBallPosition) {
      setBallPosition(finalBallPosition);
    }
    // Post-exercise save modal disabled per user request
  };

  // Global mouse event handlers for smooth dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingVertical) {
        handleVerticalDrag(e.clientY);
      }
      if (isDraggingHorizontal) {
        handleHorizontalDrag(e.clientX);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingVertical(false);
      setIsDraggingHorizontal(false);
    };

    if (isDraggingVertical || isDraggingHorizontal) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingVertical, isDraggingHorizontal, handleVerticalDrag, handleHorizontalDrag]);

  // Load saved sequences when user logs in
  useEffect(() => {
    if (user) {
      loadSavedSequences();
    }
  }, [user, loadSavedSequences]);

  // Load session metrics when user logs in
  useEffect(() => {
    const loadMetrics = async () => {
      if (user?.uid) {
        try {
          const metrics = await getUserSessionMetrics(user.uid);
          setSessionMetrics(metrics);
        } catch (error) {
          console.error('Error loading session metrics:', error);
        }
      }
    };
    loadMetrics();
  }, [user?.uid]);

  // Check WebXR support on mount
  useEffect(() => {
    const checkWebXRSupport = async () => {
      if ('xr' in navigator) {
        try {
          const supported = await (navigator as any).xr?.isSessionSupported('immersive-vr');
          setIsWebXRSupported(supported ?? false);
        } catch (error) {
          setIsWebXRSupported(false);
        }
      } else {
        setIsWebXRSupported(false);
      }
    };

    checkWebXRSupport();
  }, []);

  // Clear form errors when switching auth mode
  useEffect(() => {
    setFormError(null);
  }, [authMode]);

  // Handle Stripe checkout return
  useEffect(() => {
    const handleStripeReturn = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');

      if (!sessionId) return;

      // Prevent duplicate processing
      if (processingStripeSession.current || processedSessionId.current === sessionId) {
        return;
      }

      // Check if user needs to be logged in from pending signup
      if (!user?.uid) {
        const pendingSignup = localStorage.getItem('pending_stripe_signup');
        if (pendingSignup) {
          try {
            const {email, password, timestamp} = JSON.parse(pendingSignup);
            // Only auto-login if signup was within last 10 minutes
            if (Date.now() - timestamp < 10 * 60 * 1000) {
              console.log('Auto-logging in user after Stripe checkout...');
              processingStripeSession.current = true;
              await login(email, password);
              localStorage.removeItem('pending_stripe_signup');
              processingStripeSession.current = false;
              // Wait a bit for auth state to update, then the effect will re-run
              return;
            } else {
              localStorage.removeItem('pending_stripe_signup');
            }
          } catch (error) {
            console.error('Error auto-logging in:', error);
            localStorage.removeItem('pending_stripe_signup');
            processingStripeSession.current = false;
          }
        }
        return;
      }

      // Mark as processing and processed
      processingStripeSession.current = true;
      processedSessionId.current = sessionId;

      // User is logged in, process the Stripe session
      try {
        // Validate the Stripe session
        const validation = await validateStripeSession(sessionId);

        if (validation.valid && validation.subscription) {
          // Update user subscription in Firestore
          await updateSubscription(user.uid, {
            plan: validation.subscription.plan,
            billing: validation.subscription.billing,
            stripeCustomerId: validation.subscription.customerId,
            stripeSubscriptionId: validation.subscription.subscriptionId,
            currentPeriodEnd: validation.subscription.currentPeriodEnd,
            status: 'active',
          });
        }

        // Clear pending signup and URL parameters
        localStorage.removeItem('pending_stripe_signup');
        // Set view before navigating to ensure it persists
        setCurrentView('saved');
        // Use setTimeout to ensure state update completes before navigation
        setTimeout(() => {
          navigate('/', {replace: true});
        }, 0);
      } catch (error) {
        console.error('Error processing Stripe checkout:', error);
        // Still navigate to account view to show free plan
        setCurrentView('saved');
        setTimeout(() => {
          navigate('/', {replace: true});
        }, 0);
      } finally {
        processingStripeSession.current = false;
      }
    };

    handleStripeReturn();
  }, [location.search, user?.uid, updateSubscription, navigate, login]);

  // Create exercise sequence with current settings
  // Convert duration from minutes to seconds (-1 stays as -1 for unlimited)
  const durationInSeconds = durationMinutes === -1 ? -1 : durationMinutes * 60;
  const exerciseSequence: {type: TrainingMode; duration: number; settings?: ExerciseSettings}[] = [
    {
      type: selectedMotion,
      duration: durationInSeconds,
      settings: {
        speed,
        range,
        ballPosition,
        ...getBothEyeSettings(),
      },
    },
  ];

  // Helper functions for usage statistics
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getMostUsedExercise = (): string => {
    if (!sessionMetrics?.sessions || sessionMetrics.sessions.length === 0) return 'None';

    const exerciseCounts: Record<string, number> = {};
    sessionMetrics.sessions.forEach(session => {
      session.exercises.forEach(exercise => {
        exerciseCounts[exercise.type] = (exerciseCounts[exercise.type] || 0) + 1;
      });
    });

    const mostUsed = Object.entries(exerciseCounts).reduce(
      (max, [type, count]) => (count > max.count ? {type, count} : max),
      {type: '', count: 0}
    );

    return mostUsed.type ? motionData[mostUsed.type as TrainingMode]?.title || mostUsed.type : 'None';
  };

  return (
    <LandingPage>
      <Helmet>
        <title>OpticWave</title>
        <link rel="canonical" href={siteUrl} />
      </Helmet>

      {showVRScene ? (
        <VRScene
          onClose={handleCloseVR}
          showPreview={true}
          onHidePreview={() => {}}
          exerciseSequence={exerciseSequence}
        />
      ) : (
        <Content>
          <SideNav>
            <NavItem $active={currentView === 'active'} title="Active" onClick={() => setCurrentView('active')}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5V19L19 12L8 5Z" />
              </svg>
            </NavItem>
            <NavItem $active={currentView === 'saved'} title="Account" onClick={() => setCurrentView('saved')}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
              </svg>
            </NavItem>
          </SideNav>
          <Container>
            <TitleBar>
              {currentView === 'motions' ? (
                <BackButton onClick={() => setCurrentView('active')} title="Back to Active">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </BackButton>
              ) : (
                <div />
              )}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <AppTitle>OpticWave</AppTitle>
              </div>
              {currentView === 'guide' ? (
                <InfoButton onClick={openTutorial} title="Tutorial">
                  Guide
                </InfoButton>
              ) : currentView === 'active' && user ? (
                <SaveButton
                  onClick={() => {
                    setShowSaveModal(true);
                  }}
                >
                  Save
                </SaveButton>
              ) : (
                <div />
              )}
            </TitleBar>
            {currentView === 'saved' ? (
              user ? (
                // Logged in - show account page
                <SavedMotionsContainer>
                  {/* Account Info Section */}
                  <AccountSection>
                    <AccountHeading>Account</AccountHeading>
                    <AccountInfo>
                      <AccountLabel>Email</AccountLabel>
                      <AccountValue>
                        {user.email}
                        {profile?.subscription?.plan === 'pro' && profile?.subscription?.status === 'active' && (
                          <span
                            style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              background: 'rgba(255, 170, 68, 0.1)',
                              color: '#ffaa44',
                              border: '1px solid rgba(255, 170, 68, 0.3)',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              boxShadow: '0 0 8px rgba(255, 170, 68, 0.2), inset 0 0 4px rgba(255, 170, 68, 0.1)',
                            }}
                          >
                            PRO
                          </span>
                        )}
                      </AccountValue>
                    </AccountInfo>
                  </AccountSection>

                  {/* Upgrade Card for Free Users */}
                  {profile?.subscription?.plan !== 'pro' && (
                    <UpgradeCard>
                      <UpgradeCardHeader>Upgrade to Pro</UpgradeCardHeader>
                      <UpgradeCardText>
                        Unlock unlimited duration, custom settings, and save your motions for just $5/mo.
                      </UpgradeCardText>
                      <UpgradeButton onClick={handlePremiumCheckout}>Get Pro - $5/mo</UpgradeButton>
                    </UpgradeCard>
                  )}

                  {/* Usage Statistics Section - Only for Pro users */}
                  {/* {profile?.subscription?.plan === 'pro' && profile?.subscription?.status === 'active' && ( */}
                  {
                    <AccountSection>
                      <AccountHeading>Stats</AccountHeading>
                      <AccountInfo>
                        <StatsGrid>
                          <StatItem>
                            <AccountLabel>Total Sessions</AccountLabel>
                            <AccountValue>{sessionMetrics?.totalSessions || 0}</AccountValue>
                          </StatItem>
                          <StatItem>
                            <AccountLabel>Total Time</AccountLabel>
                            <AccountValue>{formatTime(sessionMetrics?.timeInSession?.thisYear || 0)}</AccountValue>
                          </StatItem>
                          <StatItem>
                            <AccountLabel>Current Streak</AccountLabel>
                            <AccountValue>
                              {sessionMetrics?.currentStreak || 0}{' '}
                              {sessionMetrics?.currentStreak === 1 ? 'day' : 'days'}
                            </AccountValue>
                          </StatItem>
                          <StatItem>
                            <AccountLabel>Most Used Exercise</AccountLabel>
                            <AccountValue>{getMostUsedExercise()}</AccountValue>
                          </StatItem>
                          <StatItem>
                            <AccountLabel>Last Active</AccountLabel>
                            <AccountValue>
                              {sessionMetrics?.lastSessionDate
                                ? new Date(sessionMetrics.lastSessionDate).toLocaleDateString()
                                : 'Never'}
                            </AccountValue>
                          </StatItem>
                        </StatsGrid>
                      </AccountInfo>
                    </AccountSection>
                  }

                  {/* Saved Motions Section */}
                  <AccountSection>
                    <AccountHeading>Saved Motions</AccountHeading>
                    {sequencesLoading ? (
                      <EmptyState>
                        <EmptyStateText>Loading...</EmptyStateText>
                      </EmptyState>
                    ) : savedSequences.length > 0 ? (
                      <SavedMotionsGrid>
                        {savedSequences.map(sequence => (
                          <SavedMotionCard
                            key={sequence.id}
                            onClick={() => {
                              // Load this sequence into the active view
                              if (sequence.exercises[0]) {
                                setSelectedMotion(sequence.exercises[0].type);
                                if (sequence.exercises[0].settings?.speed) {
                                  setSpeed(sequence.exercises[0].settings.speed);
                                  // Determine speed setting
                                  if (sequence.exercises[0].settings.speed <= 2) {
                                    setSpeedSetting('slow');
                                  } else if (sequence.exercises[0].settings.speed >= 14) {
                                    setSpeedSetting('fast');
                                  } else {
                                    setSpeedSetting('normal');
                                  }
                                }
                                if (sequence.exercises[0].settings?.range) {
                                  setRange(sequence.exercises[0].settings.range);
                                }
                                // Restore duration (convert from seconds to minutes)
                                const savedDurationSeconds = sequence.exercises[0].duration;
                                if (savedDurationSeconds === -1) {
                                  setDurationMinutes(-1);
                                } else {
                                  setDurationMinutes(Math.max(1, Math.round(savedDurationSeconds / 60)));
                                }
                                if (sequence.exercises[0].settings?.ballPosition) {
                                  setBallPosition(sequence.exercises[0].settings.ballPosition);
                                }
                                // Restore eye settings
                                if (
                                  sequence.exercises[0].settings?.leftEyeColor &&
                                  sequence.exercises[0].settings?.leftEyeOverlay !== undefined
                                ) {
                                  const leftState = getStateFromSettings(
                                    sequence.exercises[0].settings.leftEyeColor,
                                    sequence.exercises[0].settings.leftEyeOverlay
                                  );
                                  setLeftEyeState(leftState);
                                }
                                if (
                                  sequence.exercises[0].settings?.rightEyeColor &&
                                  sequence.exercises[0].settings?.rightEyeOverlay !== undefined
                                ) {
                                  const rightState = getStateFromSettings(
                                    sequence.exercises[0].settings.rightEyeColor,
                                    sequence.exercises[0].settings.rightEyeOverlay
                                  );
                                  setRightEyeState(rightState);
                                }
                              }
                              setCurrentView('active');
                            }}
                          >
                            <SavedMotionTitle>{sequence.name}</SavedMotionTitle>
                            <SavedMotionMeta>{new Date(sequence.createdAt).toLocaleDateString()}</SavedMotionMeta>
                            <SavedMotionSettings>
                              {sequence.exercises.map((ex, idx) => (
                                <SavedMotionTag key={idx}>{motionData[ex.type]?.title || ex.type}</SavedMotionTag>
                              ))}
                            </SavedMotionSettings>
                          </SavedMotionCard>
                        ))}
                      </SavedMotionsGrid>
                    ) : (
                      <EmptyState>
                        <EmptyStateIcon>📁</EmptyStateIcon>
                        <EmptyStateText>
                          <>
                            No saved motions yet.{' '}
                            <span
                              style={{
                                color: '#00ff88',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              onClick={() => setCurrentView('active')}
                            >
                              Create your first exercise
                            </span>{' '}
                            and click Save in the title bar!
                          </>
                        </EmptyStateText>
                      </EmptyState>
                    )}
                  </AccountSection>

                  {/* Logout Button - Fixed Position */}
                  <LogoutButton onClick={handleLogout}>Log Out</LogoutButton>
                </SavedMotionsContainer>
              ) : (
                // Not logged in - show auth form
                <SavedMotionsContainer>
                  <AccountSection>
                    <AccountHeading>{authMode === 'login' ? 'Log In' : 'Get Started'}</AccountHeading>
                    {(formError || authError) && <ErrorMessage>{formError || authError}</ErrorMessage>}
                    <AuthForm onSubmit={authMode === 'login' ? handleLogin : handleSignup}>
                      <AuthInput
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={authLoading}
                      />
                      <AuthInput
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={authLoading}
                      />
                      {authMode === 'signup' && (
                        <AuthInput
                          type="password"
                          placeholder="Confirm Password"
                          value={passwordConfirm}
                          onChange={e => setPasswordConfirm(e.target.value)}
                          disabled={authLoading}
                        />
                      )}
                      <AuthButton type="submit" disabled={authLoading} $isSignup={authMode === 'signup'}>
                        {authLoading ? 'Loading...' : 'Sign Up for Pro'}
                      </AuthButton>
                    </AuthForm>
                    <AuthToggle onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}>
                      {authMode === 'login' ? "Don't have an account? Sign up" : 'Have an account? Log in'}
                    </AuthToggle>
                  </AccountSection>

                  {authMode === 'signup' && (
                    <AccountSection>
                      <AccountHeading>Create Your Pro Account</AccountHeading>
                      <AccountInfo>
                        <GuideText style={{margin: '0 0 1rem 0', color: '#ffaa44', fontWeight: 600}}>
                          Pro is $5/mo
                        </GuideText>
                        <GuideText style={{margin: 0}}>
                          • Unlock all motions: Figure 8, Pursuit, Saccades, and Convergence
                          <br />• Save custom settings
                          <br />• Unlimited duration
                          <br />• Save your motions
                        </GuideText>
                      </AccountInfo>
                    </AccountSection>
                  )}
                </SavedMotionsContainer>
              )
            ) : currentView === 'guide' ? (
              <GuideContainer>
                <GuideSection>
                  <GuideHeading>What is OpticWave?</GuideHeading>
                  <GuideText>
                    OpticWave is a virtual reality application to help you improve eye coordination, tracking, and
                    focus. Our immersive 3D exercises let you target and strengthen specific regions of your vision.
                  </GuideText>
                </GuideSection>

                <GuideSection>
                  <GuideHeading>Pricing</GuideHeading>
                  <ComparisonTable>
                    <ComparisonCard>
                      <ComparisonTitle>Free</ComparisonTitle>
                      <ComparisonPrice>$0</ComparisonPrice>
                      <ComparisonPriceNote>No account necessary</ComparisonPriceNote>
                      <ComparisonFeatures>
                        <ComparisonFeature>Unlimited sessions</ComparisonFeature>
                        <ComparisonFeature>Customizable settings</ComparisonFeature>
                      </ComparisonFeatures>
                    </ComparisonCard>

                    <ComparisonCard $highlight $clickable onClick={handlePremiumCheckout}>
                      <ComparisonTitle>Pro</ComparisonTitle>
                      <ComparisonPrice>$5</ComparisonPrice>
                      <ComparisonPriceNote>per month</ComparisonPriceNote>
                      <ComparisonFeatures>
                        <ComparisonFeature>All motions</ComparisonFeature>
                        <ComparisonFeature>Save customized motions</ComparisonFeature>
                        <ComparisonFeature>Everything in Free</ComparisonFeature>
                      </ComparisonFeatures>
                    </ComparisonCard>
                  </ComparisonTable>
                </GuideSection>

                <GuideSection>
                  <GuideHeading>How It Works</GuideHeading>
                  <StepsGrid>
                    <StepCard>
                      <StepNumber className="step-number">1</StepNumber>
                      <div>
                        <StepTitle>Choose Exercise</StepTitle>
                        <StepDescription>Select from exercises like Convergence, Pursuit, or Saccades.</StepDescription>
                      </div>
                    </StepCard>
                    <StepCard>
                      <StepNumber className="step-number">2</StepNumber>
                      <div>
                        <StepTitle>Customize Settings</StepTitle>
                        <StepDescription>
                          Customize speed, range, and eye-specific settings to match your training needs.
                        </StepDescription>
                      </div>
                    </StepCard>
                    <StepCard>
                      <StepNumber className="step-number">3</StepNumber>
                      <div>
                        <StepTitle>Enter VR Environment</StepTitle>
                        <StepDescription>
                          Make sure you're wearing a VR headset, such as Vision Pro or Oculus. Then click Enter VR and
                          begin training.
                        </StepDescription>
                      </div>
                    </StepCard>
                  </StepsGrid>
                </GuideSection>

                <GuideSection>
                  <GuideHeading>Exercise Types</GuideHeading>
                  <ExercisesGrid>
                    {(Object.keys(motionData) as TrainingMode[]).map(motion => (
                      <ExerciseCard key={motion}>
                        <MotionIcon>{motionData[motion].icon}</MotionIcon>
                        <MotionTitle>{motionData[motion].title}</MotionTitle>
                        <MotionDescription>{motionData[motion].description}</MotionDescription>
                      </ExerciseCard>
                    ))}
                  </ExercisesGrid>
                </GuideSection>

                <GuideSection>
                  <GuideHeading>Settings Overview</GuideHeading>
                  <SettingsList>
                    <SettingItem>
                      <SettingName>Motion</SettingName>
                      <SettingDescription>
                        Choose which type of vision exercise to practice. Each targets different eye movement skills.
                      </SettingDescription>
                    </SettingItem>
                    <SettingItem>
                      <SettingName>Speed</SettingName>
                      <SettingDescription>
                        Control how fast the target moves. Start slow to build accuracy, increase speed to challenge
                        yourself.
                      </SettingDescription>
                    </SettingItem>
                    <SettingItem>
                      <SettingName>Range</SettingName>
                      <SettingDescription>
                        Adjust how far the target travels. Wider ranges provide more comprehensive training.
                      </SettingDescription>
                    </SettingItem>
                    <SettingItem>
                      <SettingName>Eye Controls</SettingName>
                      <SettingDescription>
                        Set different colors or add markers for each eye to train specific visual pathways and improve
                        eye dominance.
                      </SettingDescription>
                    </SettingItem>
                  </SettingsList>
                </GuideSection>
              </GuideContainer>
            ) : currentView === 'motions' ? (
              <MotionLibraryGrid>
                {(Object.keys(motionData) as TrainingMode[]).map(motion => (
                  <MotionCard
                    key={motion}
                    onClick={() => {
                      setSelectedMotion(motion);
                      setCurrentView('active');
                    }}
                  >
                    <MotionIcon>{motionData[motion].icon}</MotionIcon>
                    <MotionTitle>{motionData[motion].title}</MotionTitle>
                    <MotionDescription>{motionData[motion].description}</MotionDescription>
                  </MotionCard>
                ))}
              </MotionLibraryGrid>
            ) : (
              <PreviewSection data-tutorial="preview">
                {isWebXRSupported === false && showWebXRBanner && (
                  <WebXRBanner
                    style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}
                  >
                    <BannerContent>
                      <BannerDot />
                      <BannerText>
                        <strong>This browser doesn't support VR.</strong>
                        Please use a WebXR-compatible headset like the Vision Pro or Oculus.
                      </BannerText>
                    </BannerContent>
                    <BannerCloseButton onClick={() => setShowWebXRBanner(false)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </BannerCloseButton>
                  </WebXRBanner>
                )}
                <PlayButton
                  onClick={handleStartVR}
                  $isDisabled={isWebXRSupported === false}
                  data-tutorial="start"
                ></PlayButton>
                <PositionControlsOverlay>
                  <PositionIndicator>
                    X: {ballPosition.x.toFixed(2)} / Y: {ballPosition.y.toFixed(2)}
                  </PositionIndicator>
                  <VerticalPositionBar
                    ref={verticalBarRef}
                    onMouseDown={e => {
                      setIsDraggingVertical(true);
                      handleVerticalDrag(e.clientY);
                    }}
                    title="Drag to adjust vertical position"
                  >
                    <PositionHandle
                      $isDragging={isDraggingVertical}
                      style={{
                        left: '50%',
                        top: `${((1.5 - ballPosition.y) / 3) * 100}%`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      onMouseDown={e => {
                        e.stopPropagation();
                        setIsDraggingVertical(true);
                        handleVerticalDrag(e.clientY);
                      }}
                    />
                  </VerticalPositionBar>
                  <HorizontalPositionBar
                    ref={horizontalBarRef}
                    onMouseDown={e => {
                      setIsDraggingHorizontal(true);
                      handleHorizontalDrag(e.clientX);
                    }}
                    title="Drag to adjust horizontal position"
                  >
                    <PositionHandle
                      $isDragging={isDraggingHorizontal}
                      style={{
                        left: `${((ballPosition.x + 1.5) / 3) * 100}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                      onMouseDown={e => {
                        e.stopPropagation();
                        setIsDraggingHorizontal(true);
                        handleHorizontalDrag(e.clientX);
                      }}
                    />
                  </HorizontalPositionBar>
                </PositionControlsOverlay>
                {isSplitView ? (
                  <SplitViewContainer>
                    <SplitViewPanel>
                      <EyeLabel>Left Eye</EyeLabel>
                      <ExercisePreview
                        key={`left-${selectedMotion}-${speed}-${range}`}
                        exercise={selectedMotion}
                        eye="left"
                        showExercise={true}
                        settings={{
                          speed,
                          range,
                          ballPosition,
                          eyeToTrain: 'left',
                          ...getBothEyeSettings(),
                        }}
                      />
                    </SplitViewPanel>
                    <SplitViewPanel>
                      <EyeLabel>Right Eye</EyeLabel>
                      <ExercisePreview
                        key={`right-${selectedMotion}-${speed}-${range}`}
                        exercise={selectedMotion}
                        eye="right"
                        showExercise={true}
                        settings={{
                          speed,
                          range,
                          ballPosition,
                          eyeToTrain: 'right',
                          ...getBothEyeSettings(),
                        }}
                      />
                    </SplitViewPanel>
                  </SplitViewContainer>
                ) : (
                  <ExercisePreview
                    key={`preview-${selectedMotion}-${speed}-${range}`}
                    exercise={selectedMotion}
                    showExercise={true}
                    settings={{
                      speed,
                      range,
                      ballPosition,
                    }}
                  />
                )}
              </PreviewSection>
            )}

            {currentView === 'active' && (
              <Toolbar>
                {/* Motion */}
                <ControlGroup data-tutorial="motion">
                  <ControlLabel>Motion</ControlLabel>
                  <MotionSelectorWrapper>
                    <MotionSelectorCard
                      onClick={() => {
                        setShowMotionDropdown(!showMotionDropdown);
                      }}
                      style={{
                        cursor: 'pointer',
                        opacity: 1,
                      }}
                    >
                      <MotionSelectorContent>
                        <MotionSelectorTitle>{motionData[selectedMotion].title}</MotionSelectorTitle>
                        {/* {profile?.subscription?.plan !== 'pro' && <ProBadge>Pro</ProBadge>} */}
                      </MotionSelectorContent>
                      <MotionArrow $expanded={showMotionDropdown}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 15l7-7 7 7" />
                        </svg>
                      </MotionArrow>
                    </MotionSelectorCard>
                    {showMotionDropdown && (
                      <MotionDropdown>
                        {(Object.keys(motionData) as TrainingMode[]).map(motion => (
                          <MotionDropdownItem
                            key={motion}
                            $active={motion === selectedMotion}
                            onClick={() => {
                              setSelectedMotion(motion);
                              setShowMotionDropdown(false);
                            }}
                          >
                            <MotionDropdownIcon>{motionData[motion].icon}</MotionDropdownIcon>
                            {motionData[motion].title}
                          </MotionDropdownItem>
                        ))}
                      </MotionDropdown>
                    )}
                  </MotionSelectorWrapper>
                </ControlGroup>

                {/* Speed */}
                <ControlGroup data-tutorial="speed">
                  <ControlLabel>Speed</ControlLabel>
                  <SettingButtonGroup>
                    <SettingButton $active={speedSetting === 'slow'} onClick={() => handleSpeedChange('slow')}>
                      Slow
                    </SettingButton>
                    <SettingButton $active={speedSetting === 'normal'} onClick={() => handleSpeedChange('normal')}>
                      Normal
                    </SettingButton>
                    <SettingButton $active={speedSetting === 'fast'} onClick={() => handleSpeedChange('fast')}>
                      Fast
                    </SettingButton>
                  </SettingButtonGroup>
                </ControlGroup>

                {/* Range */}
                <ControlGroup data-tutorial="range">
                  <ControlLabel>Range</ControlLabel>
                  <SettingButtonGroup>
                    <SettingButton $active={range === 'narrow'} onClick={() => handleRangeChange('narrow')}>
                      Narrow
                    </SettingButton>
                    <SettingButton $active={range === 'medium'} onClick={() => handleRangeChange('medium')}>
                      Medium
                    </SettingButton>
                    <SettingButton $active={range === 'wide'} onClick={() => handleRangeChange('wide')}>
                      Wide
                    </SettingButton>
                  </SettingButtonGroup>
                </ControlGroup>

                {/* Duration */}
                <ControlGroup data-tutorial="duration">
                  <ControlLabel>Duration</ControlLabel>
                  <SettingButtonGroup>
                    <SettingButton onClick={handleDecrementDuration} disabled={durationMinutes === 1}>
                      −
                    </SettingButton>
                    <SettingButton $active style={{cursor: 'default', pointerEvents: 'none'}}>
                      {durationMinutes === -1 ? '∞' : `${durationMinutes} Min`}
                    </SettingButton>
                    <LockedSettingButton $locked={!isPro} onClick={handleIncrementDuration}>
                      +
                    </LockedSettingButton>
                    <LockedSettingButton
                      $active={durationMinutes === -1}
                      $locked={!isPro}
                      onClick={handleUnlimitedDuration}
                    >
                      ∞
                    </LockedSettingButton>
                    {!isPro && (
                      <ProOverlayWrapper>
                        <ProBadge>PRO</ProBadge>
                      </ProOverlayWrapper>
                    )}
                  </SettingButtonGroup>
                </ControlGroup>

                {/* Eye Settings */}
                <EyeControlsWrapper data-tutorial="eye-controls">
                  {/* Left Eye */}
                  <ControlGroup>
                    <ControlLabel>Left Eye</ControlLabel>
                    <EyePreviewButton $state={leftEyeState} onClick={cycleLeftEyeState} />
                  </ControlGroup>

                  {/* Right Eye */}
                  <ControlGroup>
                    <ControlLabel>Right Eye</ControlLabel>
                    <EyePreviewButton $state={rightEyeState} onClick={cycleRightEyeState} />
                  </ControlGroup>
                </EyeControlsWrapper>
              </Toolbar>
            )}

            {currentView === 'active' && user && (
              <DebugSection>
                <DebugHeader onClick={() => setShowDebug(!showDebug)}>
                  <DebugTitle>Saved Motions</DebugTitle>
                  <DebugToggle className={showDebug ? 'expanded' : ''}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </DebugToggle>
                </DebugHeader>
                {showDebug && (
                  <SavedMotionsScroll>
                    {savedSequences.length > 0 ? (
                      savedSequences
                        .slice()
                        .reverse()
                        .map(sequence => (
                          <SavedMotionCompactCard
                            key={sequence.id}
                            $isActive={selectedMotionId === sequence.id}
                            onClick={() => {
                              // Set as selected/active
                              setSelectedMotionId(sequence.id);

                              // Load this sequence into the active view
                              if (sequence.exercises[0]) {
                                setSelectedMotion(sequence.exercises[0].type);
                                if (sequence.exercises[0].settings?.speed) {
                                  setSpeed(sequence.exercises[0].settings.speed);
                                  // Determine speed setting
                                  if (sequence.exercises[0].settings.speed <= 2) {
                                    setSpeedSetting('slow');
                                  } else if (sequence.exercises[0].settings.speed >= 14) {
                                    setSpeedSetting('fast');
                                  } else {
                                    setSpeedSetting('normal');
                                  }
                                }
                                if (sequence.exercises[0].settings?.range) {
                                  setRange(sequence.exercises[0].settings.range);
                                }
                                // Restore duration (convert from seconds to minutes)
                                const savedDurationSeconds = sequence.exercises[0].duration;
                                if (savedDurationSeconds === -1) {
                                  setDurationMinutes(-1);
                                } else {
                                  setDurationMinutes(Math.max(1, Math.round(savedDurationSeconds / 60)));
                                }
                                if (sequence.exercises[0].settings?.ballPosition) {
                                  setBallPosition(sequence.exercises[0].settings.ballPosition);
                                }
                                // Restore eye settings
                                if (
                                  sequence.exercises[0].settings?.leftEyeColor &&
                                  sequence.exercises[0].settings?.leftEyeOverlay !== undefined
                                ) {
                                  const leftState = getStateFromSettings(
                                    sequence.exercises[0].settings.leftEyeColor,
                                    sequence.exercises[0].settings.leftEyeOverlay
                                  );
                                  setLeftEyeState(leftState);
                                }
                                if (
                                  sequence.exercises[0].settings?.rightEyeColor &&
                                  sequence.exercises[0].settings?.rightEyeOverlay !== undefined
                                ) {
                                  const rightState = getStateFromSettings(
                                    sequence.exercises[0].settings.rightEyeColor,
                                    sequence.exercises[0].settings.rightEyeOverlay
                                  );
                                  setRightEyeState(rightState);
                                }
                              }
                            }}
                          >
                            {selectedMotionId === sequence.id && (
                              <DeleteButton
                                onClick={e => {
                                  e.stopPropagation();
                                  setMotionToDelete({id: sequence.id, name: sequence.name});
                                  setShowDeleteModal(true);
                                }}
                              >
                                ×
                              </DeleteButton>
                            )}
                            <CompactMotionTitle>{sequence.name}</CompactMotionTitle>
                            <CompactMotionMeta>{new Date(sequence.createdAt).toLocaleDateString()}</CompactMotionMeta>
                            <CompactMotionTags>
                              {sequence.exercises.map((ex, idx) => (
                                <CompactMotionTag key={idx}>{motionData[ex.type]?.title || ex.type}</CompactMotionTag>
                              ))}
                            </CompactMotionTags>
                          </SavedMotionCompactCard>
                        ))
                    ) : (
                      <CompactEmptyState>
                        <CompactEmptyStateIcon>📁</CompactEmptyStateIcon>
                        <CompactEmptyStateText>
                          No saved motions. Click Save in the title bar to save your exercise.
                        </CompactEmptyStateText>
                      </CompactEmptyState>
                    )}
                  </SavedMotionsScroll>
                )}
              </DebugSection>
            )}
          </Container>
        </Content>
      )}

      {/* Tutorial Overlay */}
      {showTutorial && <TutorialOverlay onClose={closeTutorial} />}

      {/* Save Exercise Modal */}
      {showSaveModal && (
        <ModalOverlay onClick={() => setShowSaveModal(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeading>Save Exercise</ModalHeading>
            <ModalText>Give this exercise configuration a name so you can access it later.</ModalText>
            <ModalInput
              type="text"
              placeholder="Exercise name"
              value={sequenceName}
              onChange={e => setSequenceName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && sequenceName.trim()) {
                  handleSaveExercise();
                }
              }}
              autoFocus
            />
            <ModalActions>
              <ModalButton
                $variant="secondary"
                onClick={() => {
                  setShowSaveModal(false);
                  setSequenceName('');
                }}
              >
                Cancel
              </ModalButton>
              <ModalButton
                $variant="primary"
                onClick={handleSaveExercise}
                disabled={!sequenceName.trim() || sequencesLoading}
              >
                {sequencesLoading ? 'Saving...' : 'Save'}
              </ModalButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Delete Exercise Modal */}
      {showDeleteModal && motionToDelete && (
        <ModalOverlay
          onClick={() => {
            setShowDeleteModal(false);
            setMotionToDelete(null);
          }}
        >
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeading>Delete Exercise</ModalHeading>
            <ModalText>
              Are you sure you want to delete "{motionToDelete.name}"? This action cannot be undone.
            </ModalText>
            <ModalActions>
              <ModalButton
                $variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setMotionToDelete(null);
                }}
              >
                Cancel
              </ModalButton>
              <ModalButton
                $variant="primary"
                onClick={handleDeleteExercise}
                disabled={sequencesLoading}
                style={{
                  background: 'linear-gradient(135deg, #ff3232 0%, #cc0000 100%)',
                }}
              >
                {sequencesLoading ? 'Deleting...' : 'Delete'}
              </ModalButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Post-Exercise Save Modal */}
      {showPostExerciseSaveModal && (
        <ModalOverlay onClick={() => setShowPostExerciseSaveModal(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeading>Save This Exercise?</ModalHeading>
            <ModalText>
              Your exercise session is complete! Would you like to save this configuration with any adjustments you made
              during the session?
            </ModalText>
            <ModalInput
              type="text"
              placeholder="Exercise name"
              value={sequenceName}
              onChange={e => setSequenceName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && sequenceName.trim()) {
                  handleSaveExercise();
                }
              }}
              autoFocus
            />
            <ModalActions>
              <ModalButton
                $variant="secondary"
                onClick={() => {
                  setShowPostExerciseSaveModal(false);
                  setSequenceName('');
                }}
              >
                Skip
              </ModalButton>
              <ModalButton
                $variant="primary"
                onClick={handleSaveExercise}
                disabled={!sequenceName.trim() || sequencesLoading}
              >
                {sequencesLoading ? 'Saving...' : 'Save'}
              </ModalButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Pro Upgrade Modal */}
      {showProModal && (
        <ModalOverlay onClick={() => setShowProModal(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeading>Join Pro for $5/mo</ModalHeading>
            <ModalText>Create an account to save your settings and unlock duration settings.</ModalText>
            <ModalActions>
              <ModalButton $variant="secondary" onClick={() => setShowProModal(false)}>
                Cancel
              </ModalButton>
              <ModalButton
                $variant="primary"
                onClick={() => {
                  setShowProModal(false);
                  setCurrentView('saved');
                  setAuthMode('signup');
                }}
                style={{
                  background: 'linear-gradient(135deg, #ffaa44 0%, #ff8822 100%)',
                }}
              >
                Upgrade to Pro
              </ModalButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </LandingPage>
  );
}
